import { SpinRequestBuilder } from "ags/integration/builders/requests/spin-request-builder";
import { Components } from "appworks/components/components";
import { gameLoop } from "appworks/core/game-loop";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Layers } from "appworks/graphics/layers/layers";
import { gameState } from "appworks/model/game-state";
import { SocketConnector } from "appworks/server/connectors/socket-connector";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { UIFlag, uiFlags, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { deviceInfo } from "appworks/utils/device-info";
import { logger } from "appworks/utils/logger";
import { Timer } from "appworks/utils/timer";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { Action, AgsAutoplayConfigSchema, OperatorData, UI, UISettingsButtons, WrapperMessage } from "gameiom/wrapper/wrapper-data";
import { UIElements } from "gameiom/wrapper/wrapper-data";
import { Signal } from "signals";
import { AlertComponent } from "slotworks/components/alert/alert-component";
import { PaytableComponent } from "slotworks/components/paytable/paytable-component";
import { slotModel } from "slotworks/model/slot-model";
import { JurisdictionService } from "slotworks/services/jurisdiction/jurisdiction-service";

type ActionHandler = (data: OperatorData) => void;

class WrapperImplementation {
    public onResume: Signal = new Signal();

    protected actionList: Map<Action, ActionHandler>;

    public init(debug: boolean): void {
        if (!gameWrapper) {
            throw new Error("Can't find window.gameWrapper. Has it been included?");
        }

        this.setupErrorReporting();
        this.populateActionList();
        gameWrapper.onWrapperMessage = (action: Action, message: WrapperMessage) => this.executeAction(action, message);
        gameWrapper.getOperatorInformation();
    }

    protected onWrapperActivated(pauseId: string): void {
        this.log("Wrapper activated");
        gameLoop.setPaused(pauseId, true);

        if (!gameLoop.getPaused()) {
            return;
        }

        // Pausing the game while the wrapper stops auto plays while the reality check is active.
        uiFlags.set(UIFlag.AWAITING_RESPONSE, true);
        uiFlags.set(UIFlag.EXTERNAL_DISABLE, true);

        Services.get(CanvasService).setFullScreenPaused(true);
    }

    protected onWrapperDeactivated(pauseId: string): void {
        this.log("Wrapper deactivated");
        gameLoop.setPaused(pauseId, false);

        if (gameLoop.getPaused()) {
            return;
        }

        uiFlags.set(UIFlag.AWAITING_RESPONSE, false);
        uiFlags.set(UIFlag.EXTERNAL_DISABLE, false);

        Services.get(CanvasService).setFullScreenPaused(false);

        this.onResume.dispatch();
    }

    // TODO: Where should this be called? `settingsModel.onSessionClose` does not
    // exist any more.
    // protected wrapperShutDown(): void {
    //     commsManager.connector.close();
    // }

    protected showBalanceTooLowError(data: any): void {
        Components.get(AlertComponent).error("err.bal", false);
    }

    protected executeAction(action: Action, message: WrapperMessage): void {
        const actionToPerform = this.actionList.get(action);

        if (typeof actionToPerform === "undefined") {
            this.log(`Unknown action requested by wrapper: ${action}`);
            return;
        }

        if (actionToPerform === null) {
            return;
        }

        this.log(`Executing action: ${action}`);

        actionToPerform(message.data);
    }

    protected configureForOperator(data: OperatorData): void {
        // Disable skipping by default, it will be re-enabled by registerUiComponents if need be
        Services.get(JurisdictionService).setSpinSkippingEnabled(false);

        if (!data) {
            Services.get(JurisdictionService).setAutoplayEnabled(false);
            this.log("Operator configure returned but no data found");
            return;
        }

        if (data.ui) {
            this.registerUiComponents(data.ui);
            this.registerAutoplay(data.ui.autoplay);
        }

        if (data.operator) {
            this.registerOperator(data.operator.id);
        }
    }

    /**
     * Values come in as currency, not "cents". E.g 100.10 instead of 10010
     */
    protected updateBalance(cash: number, bonus: number): void {
        const cashInCents = cash * 100;
        const bonusInCents = bonus * 100;

        Services.get(TransactionService).setBalance(cashInCents + bonusInCents);
        gameState.setWrapperOverrideBalance(cashInCents + bonusInCents);

        gameWrapper.balancesUpdateDecimal(cash, bonus);
    }

    protected registerAutoplay(config?: AgsAutoplayConfigSchema): void {
        if (config) {
            const { autoplay } = slotModel.read();

            autoplay.autoplays.values = config.numberOfRounds?.values;
            autoplay.autoplays.defaultValue = config.numberOfRounds?.defaultVal;

            autoplay.singleWinLimit.values = config.singleWinLimit?.values || [];
            autoplay.singleWinLimit.defaultValue = config.singleWinLimit?.defaultVal;

            autoplay.lossLimit.values = config.lossLimit?.values || [];
            autoplay.lossLimit.defaultValue = config.lossLimit?.defaultVal;
            autoplay.lossLimitRequired = config.lossLimit?.lossLimitRequired;

            autoplay.totalWinLimit.values = config.totalWinLimit?.values || [];
            autoplay.totalWinLimit.defaultValue = config.totalWinLimit?.defaultVal;
            gameiomModel.displayStopOnFeatureButton = config.stopOnFeatureWin || false;

            if (config.stopOnFeatureWin) {
                autoplay.stopOnFeatureWin = false; // default toggle to false
            }

            slotModel.write({ autoplay });
        } else {
            Services.get(JurisdictionService).setAutoplayEnabled(false);
        }
    }

    protected registerUiComponents(ui: Partial<UI>): void {
        if (ui.displayClock) {
            gameiomModel.displayClock = ui.displayClock !== "disabled";
        }

        if (ui.disableFullscreen) {
            Services.get(CanvasService).fullScreenEnabled = false;
        }

        if (ui.maxBet) {
            gameiomModel.displayMaxBet = ui.maxBet !== "disabled";
        }

        if (ui.slamStop) {
            gameiomModel.slamStop = (ui.slamStop === "enabled");
        }

        if(ui.betSettings) {
            gameiomModel.betSettings = ui.betSettings;
        }

        if (ui.denomDisplay) {
            gameiomModel.denomDisplay = ui.denomDisplay;
        }

        if (ui.minStopDuration) {
            Services.get(JurisdictionService).setMinimumSpinTimes([
                { minBet: 0, delayMs: ui.minStopDuration }
            ]);
        }

        if (ui.layouts) {
            if (deviceInfo.isDesktop) {
                this.processElements(ui.layouts.desktop ?? []);
            } else {
                this.processElements(ui.layouts.mobile ?? []);
            }
        }

        if (ui.hideJackpotWording) {
            gameiomModel.hideJackpotWording = ui.hideJackpotWording !== "disabled";
        }

        if (ui.removeFromCurrencyString) {
            gameiomModel.removeFromCurrencyString = ui.removeFromCurrencyString;
        }

        if (ui.requestTimeoutDuration) {
            if (ui.requestTimeoutDuration === -1) {
                ui.requestTimeoutDuration = Infinity;
            }
            SocketConnector.REQUEST_TIMEOUT = ui.requestTimeoutDuration;
        }

        if (ui.displayWholeStakeAsDecimal) {
            gameiomModel.displayWholeStakeAsDecimal = ui.displayWholeStakeAsDecimal === "enabled";
        }

        if (ui.displayDecimalDenomAsWholeNumber) {
            gameiomModel.wholeNumberCurrencies = ui.displayDecimalDenomAsWholeNumber.split(",");
        }

        if (ui.disableSpaceToSpin) {
            gameiomModel.disableSpaceToSpin = ui.disableSpaceToSpin;
        }

        if (ui.displayType) {
            gameiomModel.displayCurrency = ui.displayType === "currency";
        }

        if (ui.hideEnableSoundsPrompt) {
            gameiomModel.hideEnableSoundsPrompt = ui.hideEnableSoundsPrompt === "enabled";
        }

        if (ui.stopOnFeatureWin) {
            gameiomModel.autoplayStopOnFeatureOverideSet = true;
            slotModel.write({ autoplay: { stopOnFeatureWin: ui.stopOnFeatureWin === "enabled" } });
        }

        if(ui.isSocial) {
            gameiomModel.isSocial = ui.isSocial;
        }
    }

    protected populateActionList(): void {
        this.actionList = new Map<Action, ActionHandler>([
            [Action.enableAudio, () => Services.get(SoundService).unmute()],
            [Action.disableAudio, () => Services.get(SoundService).mute()],
            [Action.updateBalance, (data) => this.updateBalance(data.cash, data.bonus)],
            [Action.getOperatorInformation, (data) => this.configureForOperator(data)],
            [Action.balanceTooLow, (data) => this.showBalanceTooLowError(data)],
            [Action.isWrapperActive, (data) => this.updateWrapperActive(data.active)],
            [Action.getScreenSize, null],
            [Action.balancesHasChanged, (data) => this.updateBalance(data.cash, data.bonus)],
            [Action.pauseGame, () => this.onWrapperActivated("wrapper-pause")],
            [Action.resumeGame, () => this.onWrapperDeactivated("wrapper-pause")],
            [Action.wrapperActivityChanged, (data) => this.updateWrapperActive(data.active)],
            [Action.openAbout, () => this.openHelp()],
            [Action.openHelp, () => this.openHelp()],
            [Action.openPaytable, () => this.openPaytable()],
            [Action.openPreferences, () => this.openPreferences()],
            [Action.force, (data) => this.setForce(data)],

            // These are just responses from the wrapper which we don't need to process
            [Action.gameLoadingStarted, null],
            [Action.gameLoadingEnded, null],
            [Action.stakeUpdate, null],
            [Action.balancesUpdate, null],
            [Action.loadProgressUpdate, null],
            [Action.winningsUpdate, null],
            [Action.gameRoundEnded, null],
            [Action.gotoDepositPage, null],
            [Action.gotoAccountsPage, null],
            [Action.gameRoundStarted, null],
            [Action.exitGame, null],
            [Action.menuDialogueOpened, null],
            [Action.menuClosed, null],
            [Action.betDialogueOpened, null],
            [Action.betDialogueClosed, null],
            [Action.autospinMenuOpened, null],
            [Action.autospinMenuClosed, null],
            [Action.paytableDialogueOpened, null],
            [Action.paytableDialogueClosed, null],
            [Action.insufficientFunds, null],
            [Action.bonusGameRecovering, null],
            [Action.enableFeatureMode, null],
            [Action.disableFeatureMode, null],
            [Action.gamePopupShown, null],
            [Action.gamePopupHidden, null],
            [Action.gameError, null]
        ]);
    }

    protected processElements(elements: string[]): void {
        gameiomModel.enabledSettingsButtons = [];

        elements.forEach((element: string) => {
            if (element === UISettingsButtons.RULES_BUTTON) {
                return;
            }
            
            this.processElement(element);
        });

        if (!gameiomModel.shouldDisplaySettingsButton() && elements.indexOf(UISettingsButtons.RULES_BUTTON) === -1) {
            gameiomModel.enabledSettingsButtons.push(UISettingsButtons.RULES_BUTTON);
        }
    }

    protected processElement(element: string): void {
        const availableButtons: string[] = Object.keys(UISettingsButtons).map((x) => UISettingsButtons[x]);

        if (element && availableButtons.indexOf(element) !== -1) {
            gameiomModel.enabledSettingsButtons.push(element);
        } else {
            this.log(`Trying to show UI element ${element}, but no ui element with that name was found`);
        }
    }

    protected registerOperator(id: number): void {
        gameiomModel.operatorId = id;
    }

    protected updateWrapperActive(active: boolean): void {
        if (active) {
            this.onWrapperActivated("wrapper-activated");
        } else {
            this.onWrapperDeactivated("wrapper-activated");
        }
    }

    protected openHelp(): void {
        this.openPaytable();
        const paytableContent: Layers = Layers.get("PaytableContent");

        if (!paytableContent.currentSceneIs("default")) {
            let lastPage = 1;
            while (paytableContent.hasScene(`page_${lastPage + 1}`)) {
                lastPage++;
            }
            Components.get(PaytableComponent).jumpToPage(lastPage - 1);
        }
    }

    protected openPaytable(): void {
        if (uiFlags.has(UIFlag.IDLE)) {
            uiFlags.menu(UIMenuFlag.PAYTABLE, true);
        }
    }

    protected openPreferences(): void {
        if (uiFlags.has(UIFlag.IDLE)) {
            uiFlags.menu(UIMenuFlag.MENU, true);
        }
    }

    protected setForce(forceData: any) {
        if (forceData.name === "crashgame") {
            throw new Error("Game has crashed due to crash forcer.");
        }

        SpinRequestBuilder.force = forceData.value ? JSON.stringify(forceData.value) : forceData.name;
    }

    protected setupErrorReporting(): void {
        window.addEventListener("error", (e: ErrorEvent) => {
            gameWrapper.gameError(e.error.message, e.error.stack);
        });
    }

    protected log(message: string) {
        logger.info("%c [Wrapper] ", "background: #ccc; color: #000", message);
    }
}

export const wrapperImplementation = new WrapperImplementation();
